import type {
  ExportDayOfWeek,
  ExportFrequency,
  ExportScheduleDraft,
  ExportTimeInfo,
} from '../models/export.types'

export interface ScheduleReferenceOption<V extends string | number> {
  readonly value: V
  readonly labelKey: string
}

export interface HourRangeOption extends ScheduleReferenceOption<number> {
  readonly end: number
}

export const EXPORT_FREQUENCIES: readonly ScheduleReferenceOption<ExportFrequency>[] =
  Object.freeze([
    { value: 'once', labelKey: 'dataExport.schedule.frequencies.once' },
    { value: 'daily', labelKey: 'dataExport.schedule.frequencies.daily' },
    { value: 'weekly', labelKey: 'dataExport.schedule.frequencies.weekly' },
    { value: 'monthly', labelKey: 'dataExport.schedule.frequencies.monthly' },
  ])

export const HOUR_RANGES: readonly HourRangeOption[] = Object.freeze(
  Array.from({ length: 24 }, (_, hour) => ({
    value: hour,
    end: (hour + 1) % 24,
    labelKey: `dataExport.schedule.hours.h${String(hour).padStart(2, '0')}`,
  })),
)

export const DAYS_OF_WEEK: readonly ScheduleReferenceOption<ExportDayOfWeek>[] = Object.freeze([
  { value: 'monday', labelKey: 'dataExport.schedule.days.monday' },
  { value: 'tuesday', labelKey: 'dataExport.schedule.days.tuesday' },
  { value: 'wednesday', labelKey: 'dataExport.schedule.days.wednesday' },
  { value: 'thursday', labelKey: 'dataExport.schedule.days.thursday' },
  { value: 'friday', labelKey: 'dataExport.schedule.days.friday' },
  { value: 'saturday', labelKey: 'dataExport.schedule.days.saturday' },
  { value: 'sunday', labelKey: 'dataExport.schedule.days.sunday' },
])

/** Named product defaults replace the legacy magic literals in the component body. */
export const DEFAULT_EXPORT_SCHEDULE: Readonly<ExportScheduleDraft> = Object.freeze({
  frequency: 'once',
  hour: 9,
  timezone: 'Europe/Istanbul',
  dayOfWeek: 'monday',
  dayOfMonth: 12,
})

export function availableExportFrequencies(
  allowed: readonly string[],
): readonly ScheduleReferenceOption<ExportFrequency>[] {
  const granted = new Set(allowed)
  return EXPORT_FREQUENCIES.filter((option) => granted.has(option.value))
}

/** Pure `time_info` derivation. `once` is represented by omission (`null`) at the payload seam. */
export function useScheduleTimeInfo(draft: Readonly<ExportScheduleDraft>): ExportTimeInfo | null {
  if (draft.frequency === 'once') return null

  const base: ExportTimeInfo = {
    hour: clampInteger(draft.hour, 0, 23),
    minute: 0,
    timezone: draft.timezone,
  }

  if (draft.frequency === 'weekly') return { ...base, day_of_week: draft.dayOfWeek }
  if (draft.frequency === 'monthly') {
    return { ...base, day_of_month: clampInteger(draft.dayOfMonth, 1, 31) }
  }
  return base
}

function clampInteger(value: number, min: number, max: number): number {
  const integer = Number.isFinite(value) ? Math.trunc(value) : min
  return Math.min(Math.max(integer, min), max)
}
